import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Waitlist as WaitlistEntity, Product } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { 
  Mail, 
  Search, 
  Clock, 
  CheckCircle, 
  Send,
  Eye,
  Filter
} from "lucide-react";
import { format } from "date-fns";
import { ru } from 'date-fns/locale';

const statusColors = {
  waiting: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  notified: 'bg-green-100 text-green-800 border-green-200'
};

export default function WaitlistPage() {
  const { t } = useTranslation();
  
  const statusLabels = {
    waiting: t('waitlist.waiting_status'),
    notified: t('waitlist.notified_status')
  };
  const [waitlistItems, setWaitlistItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isNotifying, setIsNotifying] = useState({});
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterItems();
  }, [waitlistItems, searchTerm, statusFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [waitlistData, productsData] = await Promise.all([
        WaitlistEntity.list('-created_date'),
        Product.list()
      ]);
      
      setWaitlistItems(waitlistData);
      setProducts(productsData);
    } catch (error) {
      console.error("Error loading waitlist data:", error);
    }
    setIsLoading(false);
  };

  const filterItems = () => {
    let filtered = waitlistItems;

    if (searchTerm) {
      filtered = filtered.filter(item =>
        (item.product_name || item.item_name)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    setFilteredItems(filtered);
  };

  const getProductName = (itemId) => {
    const product = products.find(p => p.id === itemId);
    return product?.name || t('waitlist.product_not_found');
  };

  const buildDefaultSubject = (productName) =>
    t('waitlist.default_email_subject', { product: productName });

  const buildDefaultBody = (productName) =>
    t('waitlist.default_email_body', {
      product: productName,
      storeUrl: `${window.location.origin}/BookStore`
    });

  const openNotifyModal = (item) => {
    if (isNotifying[item.id]) return;
    const productName = item.item_name || item.product_name || getProductName(item.item_id);
    setSelectedEntry(item);
    setEmailSubject(buildDefaultSubject(productName));
    setEmailBody(buildDefaultBody(productName));
    setNotifyModalOpen(true);
  };

  const handleBulkNotify = async () => {
    const waitingItems = filteredItems.filter(item => item.status === 'waiting');
    if (waitingItems.length === 0) {
      alert(t('waitlist.no_customers_to_notify'));
      return;
    }

    if (!confirm(`${t('waitlist.confirm_notify_all')} ${waitingItems.length} ${t('waitlist.customers')}?`)) {
      return;
    }

    for (const item of waitingItems) {
      try {
        const productName = item.item_name || item.product_name || getProductName(item.item_id);
        await WaitlistEntity.notifyCustomer(item.id, {
          subject: buildDefaultSubject(productName),
          message: buildDefaultBody(productName)
        });
      } catch (error) {
        console.error('Error notifying waitlist entry:', error);
        alert(t('waitlist.notification_error'));
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    await loadData();
  };

  const handleSendNotification = async () => {
    if (!selectedEntry) return;
    setIsSendingNotification(true);
    setIsNotifying(prev => ({ ...prev, [selectedEntry.id]: true }));
    try {
      await WaitlistEntity.notifyCustomer(selectedEntry.id, {
        subject: emailSubject,
        message: emailBody
      });
      await loadData();
      toast({
        title: t('waitlist.notification_sent'),
        description: selectedEntry.customer_email
      });
      setNotifyModalOpen(false);
      setSelectedEntry(null);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: t('waitlist.notification_error'),
        description: error.message || t('waitlist.notification_error'),
        variant: 'destructive'
      });
    } finally {
      setIsSendingNotification(false);
      setIsNotifying(prev => ({ ...prev, [selectedEntry?.id]: false }));
    }
  };

  return (
    <div className="min-h-screen gradient-bg p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-start">
          <div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('waitlist.title')}</h1>
        <p className="text-slate-600 font-medium">{t('waitlist.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleBulkNotify}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={filteredItems.filter(item => item.status === 'waiting').length === 0}
            >
              <Send className="w-4 h-4 mr-2" />
              {t('waitlist.notify_all')}
            </Button>
          </div>
        </div>

        {/* Статистика */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="elegant-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('waitlist.total_requests')}</p>
                  <p className="text-3xl font-bold text-slate-900">{waitlistItems.length}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="elegant-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('waitlist.pending_notifications')}</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {waitlistItems.filter(item => item.status === 'waiting').length}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-amber-100">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="elegant-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('waitlist.notified_count')}</p>
                  <p className="text-3xl font-bold text-green-600">
                    {waitlistItems.filter(item => item.status === 'notified').length}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-green-100">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Фильтры */}
        <Card className="elegant-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder={t('waitlist.search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Фильтр по статусу" />
                  </SelectTrigger>
                  <SelectContent>
                                    <SelectItem value="all">{t('waitlist.all_statuses')}</SelectItem>
                <SelectItem value="waiting">{t('waitlist.waiting_status')}</SelectItem>
                <SelectItem value="notified">{t('waitlist.notified_status')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Таблица */}
        <Card className="elegant-card overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-bold text-slate-900">{t('waitlist.product')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('waitlist.customer_email')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('waitlist.request_date')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('status.status')}</TableHead>
                  <TableHead className="font-bold text-slate-900">{t('waitlist.notification_date')}</TableHead>
                  <TableHead className="font-bold text-slate-900 text-right">{t('waitlist.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      Загрузка...
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <Mail className="w-12 h-12 text-slate-300" />
                                                  <p className="font-medium">{t('waitlist.requests_not_found')}</p>
                        <p className="text-sm">{t('waitlist.empty_description')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map(item => (
                    <TableRow key={item.id} className="hover:bg-slate-50/50">
                      <TableCell>
                        <p className="font-medium text-slate-900">
                          {item.item_name || item.product_name || getProductName(item.item_id)}
                        </p>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {item.customer_email}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {format(new Date(item.created_date), 'dd.MM.yyyy HH:mm', { locale: ru })}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[item.status]}>
                          {statusLabels[item.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {item.notified_at ? 
                          format(new Date(item.notified_at), 'dd.MM.yyyy HH:mm', { locale: ru }) : 
                          '-'
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        {item.status === 'waiting' && (
                          <Button
                            size="sm"
                            onClick={() => openNotifyModal(item)}
                            disabled={isNotifying[item.id]}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            {t('waitlist.notify_customer')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      <Dialog open={notifyModalOpen} onOpenChange={(open) => {
        if (!open && !isSendingNotification) {
          setNotifyModalOpen(false);
          setSelectedEntry(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('waitlist.notify_modal_title')}</DialogTitle>
            <DialogDescription>
              {selectedEntry?.customer_email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="notify-subject">{t('waitlist.notify_modal_subject')}</Label>
              <Input
                id="notify-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="notify-body">{t('waitlist.notify_modal_body')}</Label>
              <Textarea
                id="notify-body"
                className="min-h-[160px]"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (!isSendingNotification) {
                  setNotifyModalOpen(false);
                  setSelectedEntry(null);
                }
              }}
            >
              {t('waitlist.notify_modal_cancel')}
            </Button>
            <Button
              onClick={handleSendNotification}
              disabled={isSendingNotification || !emailSubject.trim() || !emailBody.trim()}
            >
              {isSendingNotification ? t('notifications.info.sending') : t('waitlist.notify_modal_send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}